/**
 * Stock Market Data service (Fundamental Analysis Module — Phases 1-3).
 *
 * Talks only to this app's own FastAPI backend (`/api/v1/stocks/*`) — the
 * frontend never calls ICICI Breeze directly (Phase 1 spec requirement
 * #11), and never calculates indicators or Breakout Scores itself —
 * it only reads what the backend's Indicator Engine / canonical Breakout
 * Score Engine have already computed and stored. Read methods work for any
 * authenticated user; the resolve/import/recompute methods hit admin-only
 * backend routes, so calling them as a non-admin user will surface a 403
 * via the shared axios error handling.
 */

import api from "@/services/api";
import type {
  AdminOperationStatusRead,
  BreakoutStatus,
  ComputeSummary,
  DataImportLog,
  EODImportStatusResponse,
  HistoricalPrice,
  ImportAndScoreAllResponse,
  ImportAndScoreResponse,
  MarketScannerListResponse,
  PaginatedResponse,
  RecomputeAllResponse,
  RecomputeResponse,
  ResolveAllBreezeCodesResponse,
  ResolveBreezeCodeResponse,
  StockHistoryResponse,
  StockMaster,
  StockMasterListResponse,
} from "@/types";

export interface StockPriceSearchParams {
  interval?: string;
  trade_date_from?: string;
  trade_date_to?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface ImportTriggerBody {
  from_date: string;
  to_date: string;
  interval?: string;
}

export interface ScannerSearchParams {
  search?: string;
  sector?: string;
  status?: BreakoutStatus;
  breakout_score_min?: number;
  breakout_score_max?: number;
  rsi_min?: number;
  rsi_max?: number;
  price_min?: number;
  price_max?: number;
  volume_min?: number;
  volume_max?: number;
  fno_only?: boolean;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export const stockMarketService = {
  async listStocks(): Promise<StockMasterListResponse> {
    const { data } = await api.get<StockMasterListResponse>("/stocks");
    return data;
  },

  // Searches the FULL catalog (active or not — includes the ~9,500+ stocks
  // added by the bulk NSE/BSE seed script), unlike listStocks() above which
  // only returns the active universe. Powers Portfolio Tracker's Add Stock
  // autocomplete, which can no longer preload the whole catalog client-side.
  async searchStocks(q: string, limit = 20): Promise<StockMasterListResponse> {
    const { data } = await api.get<StockMasterListResponse>("/stocks/search", { params: { q, limit } });
    return data;
  },

  async getStock(symbol: string): Promise<StockMaster> {
    const { data } = await api.get<StockMaster>(`/stocks/${symbol}`);
    return data;
  },

  async listPrices(
    symbol: string,
    params: StockPriceSearchParams = {}
  ): Promise<PaginatedResponse<HistoricalPrice>> {
    const { data } = await api.get<PaginatedResponse<HistoricalPrice>>(`/stocks/${symbol}/prices`, {
      params,
    });
    return data;
  },

  async listImportLog(symbol?: string, limit = 50): Promise<DataImportLog[]> {
    const path = symbol ? `/stocks/${symbol}/import-log` : "/stocks/import-log";
    const { data } = await api.get<DataImportLog[]>(path, { params: { limit } });
    return data;
  },

  // ---- Market Scanner reads (Phase 2/3 — any authenticated user) ----

  async listScanner(params: ScannerSearchParams = {}): Promise<MarketScannerListResponse> {
    const { data } = await api.get<MarketScannerListResponse>("/stocks/scanner", { params });
    return data;
  },

  async getStockHistory(symbol: string, months = 6): Promise<StockHistoryResponse> {
    const { data } = await api.get<StockHistoryResponse>(`/stocks/scanner/${symbol}/history`, {
      params: { months },
    });
    return data;
  },

  // ---- admin-only (backend enforces `require_roles("ADMIN")`) ----

  async resolveBreezeCode(symbol: string): Promise<ResolveBreezeCodeResponse> {
    const { data } = await api.post<ResolveBreezeCodeResponse>(`/stocks/${symbol}/resolve-breeze-code`);
    return data;
  },

  async resolveAllBreezeCodes(): Promise<ResolveAllBreezeCodesResponse> {
    const { data } = await api.post<ResolveAllBreezeCodesResponse>("/stocks/resolve-breeze-codes");
    return data;
  },

  async importStock(symbol: string, body: ImportTriggerBody): Promise<ImportAndScoreResponse> {
    const { data } = await api.post<ImportAndScoreResponse>(`/stocks/${symbol}/import`, body);
    return data;
  },

  async importAll(body: ImportTriggerBody): Promise<ImportAndScoreAllResponse> {
    const { data } = await api.post<ImportAndScoreAllResponse>("/stocks/import-all", body);
    return data;
  },

  async recomputeStock(symbol: string): Promise<RecomputeResponse> {
    const { data } = await api.post<RecomputeResponse>(`/stocks/${symbol}/recompute`);
    return data;
  },

  async recomputeAllActive(): Promise<RecomputeAllResponse> {
    const { data } = await api.post<RecomputeAllResponse>("/stocks/recompute-all");
    return data;
  },

  // Automated EOD Stock Data Foundation (2026-09-13) — read-only status,
  // never triggers an import. See app.api.v1.stock_market.get_eod_import_status.
  async getEodImportStatus(): Promise<EODImportStatusResponse> {
    const { data } = await api.get<EODImportStatusResponse>("/stocks/eod-status");
    return data;
  },

  // Data Operations page "last run" status (2026-09-19) — read-only, never
  // triggers an import. See app.api.v1.stock_market.get_import_all_status.
  async getImportAllStatus(): Promise<AdminOperationStatusRead | null> {
    const { data } = await api.get<AdminOperationStatusRead | null>("/stocks/import-all-status");
    return data;
  },
};

export type { ComputeSummary };
export default stockMarketService;
